import { narrativeStatus } from '@helpers/narratives';

import {
  VizGroupedHorizontalBar,
  VizGroupedVerticalBar,
  VizHorizontalBar,
  VizScatter,
  VizSimpleTree,
  VizStackedHorizontalBar,
  VizSummaryCards,
  VizTable,
  VizTree,
  VizVerticalBar,
} from '@utilities/icons/narrative';
import { DEFAULT_DRAFT_NARRATIVE_NAME } from './constants';

const DEFAULT_PROMPT_NAME = 'Narrative Prompt';

export const EMPTY_DOWNLOAD_MAP = new Map();

/**
 * Formats a date string to a label.
 * @param {string} isoDate - The ISO date string.
 * @returns {string|null} - The formatted date label or null if the date is invalid.
 */
const formatDateLabel = (isoDate) => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Builds a flow data object for a draft flow state.
 * @param {Object} flowState - The flow state object.
 * @param {Array} componentIds - An array of component/topic IDs to include in the flow data, also updated with manual selections.
 * @returns {Object|null} - The flow data object or null if flowState is not provided.
 */
export const buildDraftFlowData = (flowState, componentIds) => {
  if (!flowState) return null;
  const narrativeName = flowState?.narrativeName || DEFAULT_DRAFT_NARRATIVE_NAME;

  return {
    answers: {
      ...(flowState?.defaultPromptAnswers || {}),
      ...(flowState?.readOnlyPromptAnswers || {}),
      ...(flowState?.editablePromptAnswers || {}),
    },
    createdDatetime: formatDateLabel(flowState?.createdDatetime || flowState?.dateRangeStart),
    id: null,
    isReadOnly: true,
    name: narrativeName,
    narrative_id: flowState?.narrativeId || null,
    narrativeName,
    topic_ids: componentIds || null,
    topics: [],
  };
};

/**
 * Builds topic data for a draft component.
 * @param {Object} draft - The draft component object.
 * @returns {Object} - The topic data object.
 */
export const buildDraftTopicData = (draft) => {
  const resolvedTitle = draft?.cleanTitle || draft?.title;
  const hasDraftDisplayData = Boolean(draft?.dataset?.isDraftDisplayData);
  const dataset = draft?.dataset
    ? {
        ...draft.dataset,
        clean_title: resolvedTitle,
        readAs: draft.dataset?.readAs ?? draft.readAs,
        title: resolvedTitle,
        type: draft.dataset?.type,
      }
    : null;

  return {
    error: false,
    isHidden: false,
    loading: !hasDraftDisplayData,
    tags: {},
    title: resolvedTitle,
    topicId: null,
    visualization: {
      clean_title: resolvedTitle,
      datasets: dataset ? [dataset] : [],
      sample_size: draft?.sampleSize || null,
    },
  };
};

/**
 * Builds a topic shell object for a draft component.
 * @param {Object} draft - The draft component object.
 * @returns {Object} - The topic shell object.
 */
export const buildTopicShell = (draft) => ({
  feedback: {},
  id: draft.componentId,
  isHidden: false,
  status: narrativeStatus.COMPLETED,
  tags: {},
  title: draft.title,
});

const normalizePromptAttribute = (attribute = {}, fallbackId) => ({
  field: attribute?.field ?? attribute?.id ?? attribute?.attributeId ?? fallbackId,
  id: attribute?.id ?? attribute?.attributeId ?? fallbackId,
  isCustom: Boolean(attribute?.isCustom ?? attribute?.is_custom ?? attribute?.type === 'custom'),
  name: attribute?.name || '',
  type: attribute?.type ?? null,
});

export const buildEditablePromptsFromFlowState = (flowState) => {
  const requirements = Array.isArray(flowState?.editablePromptRequirements)
    ? flowState.editablePromptRequirements
    : [];

  return requirements.map((requirement, index) => {
    const promptKey = requirement?.id || `prompt-${index}`;
    const attributes =
      requirement?.module?.attributes?.map((attribute, attributeIndex) =>
        normalizePromptAttribute(attribute, `${promptKey}-${attributeIndex}`)
      ) || [];
    const isDateRange = promptKey === 'date_range';

    return {
      attributes,
      ...(isDateRange
        ? {
            dateOptions:
              requirement?.module?.dateOptions?.map(({ options = [], timeFrame, type }) => ({
                options,
                timeFrame,
                type,
              })) ||
              requirement?.module?.date_options?.map(
                ({ options = [], time_frame: timeFrame, tooltip }) => ({
                  options: options.map(({ date_range: dateRange, label }) => ({
                    label,
                    value: dateRange,
                  })),
                  timeFrame,
                  type: tooltip,
                })
              ) ||
              [],
          }
        : {}),
      emptyValue:
        requirement?.emptyValue ||
        requirement?.empty_value ||
        `Add ${requirement?.name || DEFAULT_PROMPT_NAME}`,
      id: promptKey,
      isAdvanced: Boolean(requirement?.isAdvanced ?? requirement?.advanced ?? false),
      isDateRange,
      isPinnable: Boolean(requirement?.isPinnable ?? requirement?.is_pinnable ?? false),
      isPrefixIncluded: Boolean(
        requirement?.isPrefixIncluded ?? requirement?.is_prefix_included ?? false
      ),
      isReadOnly: Boolean(requirement?.isReadOnly ?? requirement?.read_only ?? false),
      isRequired: Boolean(requirement?.required),
      isRequiredForModal: Boolean(requirement?.required),
      isSearchEnabled: Boolean(requirement?.isSearchEnabled ?? requirement?.search_enabled ?? true),
      isSortEnabled: Boolean(requirement?.isSortEnabled ?? requirement?.sort_enabled ?? false),
      maxValues: requirement?.max ?? requirement?.module?.max ?? null,
      minAnswers: requirement?.min ?? requirement?.module?.min ?? null,
      name: requirement?.name || DEFAULT_PROMPT_NAME,
      promptKey,
      shortTitle:
        requirement?.shortTitle ||
        requirement?.short_title ||
        requirement?.name ||
        DEFAULT_PROMPT_NAME,
      title: requirement?.title || requirement?.name || DEFAULT_PROMPT_NAME,
      type: requirement?.module?.type || requirement?.type || null,
    };
  });
};

export const extractEditablePromptAnswers = (flowState) => {
  if (!flowState || typeof flowState !== 'object') return {};
  const rawAnswers = {
    ...(flowState?.defaultPromptAnswers || {}),
    ...(flowState?.editablePromptAnswers || {}),
  };
  if (!rawAnswers || typeof rawAnswers !== 'object') return {};
  return Object.keys(rawAnswers).reduce((acc, key) => {
    const value = rawAnswers[key];
    acc[key] = Array.isArray(value) ? [...value] : value;
    return acc;
  }, {});
};

export const hasPromptRequirements = (flowState) => {
  return Array.isArray(flowState?.editablePromptRequirements)
    ? flowState.editablePromptRequirements.length > 0
    : false;
};

export const areRequiredPromptsFilled = (prompts, transformedAnswersMap) => {
  if (!Array.isArray(prompts) || prompts.length === 0) return true;

  const requiredPrompts = prompts.filter(
    (prompt) => prompt.isRequired || prompt.isRequiredForModal
  );

  if (!requiredPrompts.length) return true;

  return requiredPrompts.every((prompt) => {
    const answers = transformedAnswersMap?.[prompt.promptKey];
    return Array.isArray(answers) ? answers.length > 0 : Boolean(answers);
  });
};

/** Gets appropriate icon for topic library card based on viz type
 * @param {string} vizType
 * @returns {JSX.Element | null}
 */

export const getTopicLibraryCardIcon = (vizType) => {
  switch (vizType) {
    case 'bar-chart':
      return <VizHorizontalBar />;
    case 'bar-chart-two-labels':
      return <VizGroupedHorizontalBar />;
    case 'bar-stack-chart':
      return <VizStackedHorizontalBar />;
    case 'brand-tree-v2':
      return <VizTree />;
    case 'column-chart':
      return <VizVerticalBar />;
    case 'ebsova-tree':
      return <VizSimpleTree />;
    case 'grouped-vertical-bar-chart':
      return <VizGroupedVerticalBar />;
    case 'incrementality-source-of-volume':
      return <VizTree />;
    case 'scatter-chart':
      return <VizScatter />;
    case 'summary-card':
      return <VizSummaryCards />;
    case 'table':
      return <VizTable />;
    default:
      return null;
  }
};
